'use client';

/**
 * RecentlyViewedTracker — invisible client component that records a
 * product visit to the localStorage recently-viewed cache (M-7).
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item M-7.
 *
 * Why a separate client component?
 *   The product detail page (`/market/producto/[storeSlug]/[productSlug]`)
 *   is a Server Component (ISR + `generateMetadata`). Server Components
 *   can't read or write `localStorage` — it's a browser-only API. This
 *   tiny client component mirrors the `ReferralRefCapture` pattern:
 *   rendered as a child of the Server Component, renders nothing
 *   visible, runs an effect on mount that writes to localStorage.
 *
 * Behavior (per M-7 spec):
 *   - On mount, calls `saveRecentlyViewed(...)` with the product's
 *     minimal payload (productId, slug, storeSlug, title, priceCredits,
 *     images[0], type, category, rating, sellerStoreName,
 *     singlePurchaseRecurring).
 *   - `saveRecentlyViewed` dedupes by `productId`, prepends the item
 *     (most-recent-first), + trims to 12 entries.
 *   - SSR-safe (no-op on the server).
 *   - No layout shift (renders `null`).
 *
 * §6 Anti-Invención:
 *   - The localStorage key + dedupe logic live in `@/lib/recently-viewed`
 *     (single source of truth). This component is a thin caller.
 *   - NO `as any` (P-041). NO `console.*` (P-042).
 */

import { useEffect } from 'react';
import { saveRecentlyViewed } from '@/lib/recently-viewed';
import type { PublicProductResponse } from '@profileia/shared';

export interface RecentlyViewedTrackerProps {
  /** The public product being viewed. Only the rail-relevant fields
   *  are extracted + persisted to localStorage. */
  product: PublicProductResponse;
}

/**
 * Render nothing visible. Side-effect-only client component — runs an
 * effect on mount that writes to localStorage. Returns `null` so it
 * doesn't affect layout / DOM.
 */
export function RecentlyViewedTracker({ product }: RecentlyViewedTrackerProps) {
  useEffect(() => {
    saveRecentlyViewed({
      productId: product.productId,
      slug: product.slug,
      storeSlug: product.storeSlug,
      title: product.title,
      priceCredits: product.priceCredits,
      images: product.images.slice(0, 1),
      type: product.type,
      category: product.category,
      rating: product.rating,
      sellerStoreName: product.seller.storeName,
      singlePurchaseRecurring: product.singlePurchaseRecurring,
    });
    // Intentionally run only on mount — `productId` is stable per page
    // (the URL changes → Next.js remounts). We don't want to re-save
    // if `rating` changes mid-session (the spec says "on mount").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.productId]);

  return null;
}
